var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { Button, FlexBox, Icon, IconButton, Skeleton, Text, Tooltip, } from '@dixa/lib-shared-habla';
import { colors } from '@dixa/lib-shared-tokens';
import classnames from 'classnames';
import { memo, useCallback, useMemo, useState } from 'react';
import { aiApi } from 'apis/ai';
import { appActions } from 'app/app.actions';
import { AiBanner } from 'components/aiBanner';
import { articles } from 'utils/elevio';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import styles from './conversationSummary.module.scss';
export const ConversationSummary = memo(function ConversationSummary({ className, feedbackData, isFetching, isStale, summary, onFeedback, onRequestRegenerate, }) {
    const dispatch = useAppDispatch();
    const [feedback, setFeedback] = useState(null);
    const handleFeedback = useCallback((options) => {
        onFeedback === null || onFeedback === void 0 ? void 0 : onFeedback(options);
        setFeedback(options.isUseful ? 'useful' : 'not-useful');
        if (summary === null || summary === void 0 ? void 0 : summary.feedback_id) {
            dispatch(aiApi.endpoints.createAiFeedback.initiate({
                feedbackId: summary.feedback_id,
                feedback: [
                    { type: 'rating', value: options.isUseful ? 'good' : 'bad' },
                ],
            }));
        }
    }, [onFeedback, summary, dispatch]);
    const handleThumbsUp = useCallback(() => {
        handleFeedback({ isUseful: true });
    }, [handleFeedback]);
    const handleThumbsDown = useCallback(() => {
        handleFeedback({ isUseful: false });
    }, [handleFeedback]);
    const handleRegenerate = useCallback(() => {
        setFeedback(null);
        onRequestRegenerate === null || onRequestRegenerate === void 0 ? void 0 : onRequestRegenerate();
    }, [onRequestRegenerate]);
    const handleAdditionalFeedback = useCallback(() => __awaiter(this, void 0, void 0, function* () {
        if (!feedback)
            return;
        const { isSent, feedbackMessage } = yield dispatch(appActions.showModal({
            modal: {
                type: 'app:feedbackForm',
                arg: {
                    title: 'Conversation summary feedback',
                    team: 'conversations',
                    placeholder: feedback === 'useful'
                        ? 'What do you like about the summary?'
                        : 'What was the issue with the summary? How could it be improved?',
                    extra: Object.assign({ feedback }, feedbackData),
                },
            },
        }));
        if (isSent && feedbackMessage && (summary === null || summary === void 0 ? void 0 : summary.feedback_id)) {
            dispatch(aiApi.endpoints.createAiFeedback.initiate({
                feedbackId: summary.feedback_id,
                feedback: [
                    {
                        type: 'rating',
                        value: feedback === 'useful' ? 'good' : 'bad',
                    },
                    {
                        type: 'comment',
                        value: feedbackMessage,
                    },
                ],
            }));
        }
    }), [dispatch, feedback, feedbackData, summary === null || summary === void 0 ? void 0 : summary.feedback_id]);
    const summaryContent = useMemo(() => {
        if (isFetching) {
            return (_jsxs(_Fragment, { children: [_jsx(Skeleton, { className: styles.skeleton, width: "100%" }), _jsx(Skeleton, { className: styles.skeleton, maxWidth: "400px" })] }));
        }
        return (_jsx(Text.Paragraph, Object.assign({ className: styles.content }, { children: (summary === null || summary === void 0 ? void 0 : summary.summary) || 'No summary is available for this conversation.' })));
    }, [isFetching, summary === null || summary === void 0 ? void 0 : summary.summary]);
    const feedbackSection = useMemo(() => {
        if (isFetching) {
            return (_jsx(Skeleton, { className: styles.skeleton, width: "80px", height: "32px" }));
        }
        return (_jsxs(FlexBox, Object.assign({ gap: "200" }, { children: [_jsx(Tooltip, Object.assign({ dark: true, content: "Regenerate summary" }, { children: _jsx(IconButton, { onClick: handleRegenerate, variant: "tertiary", icon: "sync" }) })), feedback ? (_jsx(Button, Object.assign({ variant: "tertiary", icon: feedback === 'useful' ? 'thumbs-up' : 'thumbs-down', onClick: handleAdditionalFeedback }, { children: "Tell us more" }))) : (_jsxs(_Fragment, { children: [_jsx(Tooltip, Object.assign({ dark: true, content: "This summary is not helpful" }, { children: _jsx(IconButton, { onClick: handleThumbsDown, variant: "tertiary", icon: "thumbs-down" }) })), _jsx(Tooltip, Object.assign({ dark: true, content: "This summary is helpful" }, { children: _jsx(IconButton, { onClick: handleThumbsUp, variant: "tertiary", icon: "thumbs-up" }) }))] }))] })));
    }, [
        isFetching,
        handleRegenerate,
        feedback,
        handleAdditionalFeedback,
        handleThumbsDown,
        handleThumbsUp,
    ]);
    const summaryHeader = (_jsxs(Text.Paragraph, Object.assign({ className: styles.title }, { children: [_jsxs("strong", { children: ["Summary generated by AI", isStale && ' (so far)', "."] }), ' ', _jsx("span", Object.assign({ className: styles.elevioLink, onClick: () => window._elev.openArticle(articles.conversations.summaryByAI) }, { children: "Learn more" }))] })));
    return (_jsxs(AiBanner, Object.assign({ className: classnames(styles.root, className) }, { children: [_jsx(Icon, { color: colors.purple[600], name: "ai" }), _jsxs(FlexBox, Object.assign({ flexDirection: "column" }, { children: [summaryHeader, summaryContent] })), _jsx(FlexBox, Object.assign({ alignItems: "center", justifyContent: "center" }, { children: feedbackSection }))] })));
});
